import { TaskState, deleteObject, ref, uploadBytesResumable } from 'firebase/storage';
import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { storage } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { AttachedFile } from '../../types/briefing';
import { Icon } from '../Icon';
import { Label } from '../Label';
import { Loading } from '../Loading';
import { Modal } from '../Modal';

export interface FileUploadProps {
  /** Text to show on the upload button */
  label: string;
  initialFiles?: AttachedFile[];
  /**
   * Callback that provides list of successfully uploaded attachments
   * Should be a `useCallback` wrapped function in most cases to avoid an
   * infinite loop.
   */
  onChange?: (files: AttachedFile[]) => void;
}

export type AttachedFileState = {
  file: AttachedFile;
  state: TaskState;
};

export const FileUpload: FC<FileUploadProps> = ({
  label,
  initialFiles,
  onChange,
}: FileUploadProps) => {
  const [{ auth }] = useAuth();
  const { t } = useTranslation();

  const fileUploadRef = useRef(null);
  const fileUploadFormRef = useRef(null);

  const [currentFileStates, setCurrentFileStates] = useState<
    AttachedFileState[]
  >(
    initialFiles
      ? initialFiles.map((file) => ({ file: file, state: 'success' }))
      : [],
  );
  const [isModalHidden, setIsModalHidden] = useState(true);
  const [fileToBeDeleted, setFileToBeDeleted] =
    useState<AttachedFileState | null>(null);

  useEffect(() => {
    if (onChange) {
      onChange(
        currentFileStates
          .filter((attachment) => attachment.state === 'success')
          .map((attachment) => attachment.file),
      );
    }
  }, [currentFileStates, onChange]);

  const deleteFile = useCallback(
    (fileState: AttachedFileState | null) => {
      if (fileState) {
        deleteObject(ref(storage, fileState.file.path)).then(() => {
          setCurrentFileStates((fileStates) =>
            fileStates.filter((fs) => fs.file.id !== fileState.file.id),
          );
          setIsModalHidden(true);
        })
      } else {
        console.error('File not found');
      }
    },
    [setCurrentFileStates],
  );

  /** Updates the status in `currentFileStates` for the file with the given id. */
  const updateFileStatus = useCallback(
    (id: string, state: TaskState) => {
      setCurrentFileStates((files) =>
        files.map((attachment) => {
          if (attachment.file.id === id) {
            return { file: attachment.file, state: state };
          } else {
            return attachment;
          }
        }),
      );
    },
    [setCurrentFileStates],
  );

  /** Appends a counter to the end of a file name,
   *  if another file with this name already exists. */
  const deduplicateFilename = (
    filename: string,
    existingFilenames: string[],
  ) => {
    const makeCounterFilename = (filename: string, counter: number) => {
      const components = filename.split('.');
      components[0] += ` (${counter})`;
      return components.join('.');
    };

    let adjustedFilename = filename;
    let counter = 0;

    while (existingFilenames.includes(adjustedFilename)) {
      counter++;
      adjustedFilename = makeCounterFilename(filename, counter);
    }

    return adjustedFilename;
  };

  const uploadFile = useCallback(
    async (localFile: File, fileState: AttachedFileState) => {
      uploadBytesResumable(ref(storage, fileState.file.path), localFile)
        .on("state_changed", () => {
          updateFileStatus(
              fileState.file.id,
              'running'
            );
        }, (error) => {
          updateFileStatus(
            fileState.file.id,
            'error'
          );
          console.error(error)
        }, () => {
          updateFileStatus(
            fileState.file.id,
            'success'
          );
        });
    },
    [updateFileStatus],
  );

  const onFileSelected = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files ? event.target.files : null;

      if (files && files.length > 0) {
        const existingFilenames = currentFileStates.map(
          (fileState) => fileState.file.adjustedName,
        );
        const newFiles: AttachedFileState[] = [];

        for (const file of files) {
          const fileId = uuidv4();
          const adjustedFilename = deduplicateFilename(
            file.name,
            existingFilenames,
          );
          existingFilenames.push(adjustedFilename);
          const attachedFile: AttachedFileState = {
            file: {
              id: fileId,
              originalName: file.name,
              adjustedName: adjustedFilename,
              path: `userUploads/${auth.uid}/${fileId}/${adjustedFilename}`,
            },
            state: 'running',
          };
          newFiles.push(attachedFile);
        }

        setCurrentFileStates([...currentFileStates, ...newFiles]);

        for (let i = 0; i < files.length; i++) {
          uploadFile(files[i], newFiles[i]);
        }
      }
    },
    [auth.uid, currentFileStates, uploadFile],
  );

  const showDeleteDialog = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const file = currentFileStates.find(
        (fs) => fs.file.id === event.currentTarget.getAttribute('data-target'),
      );
      if (file) {
        setFileToBeDeleted(file);
      } else {
        console.error("Didn't find file for selection");
      }
      setIsModalHidden(false);
    },
    [currentFileStates],
  );

  const getUploadFeedback = useCallback(
    (fileState: AttachedFileState) => {
      switch (fileState.state) {
        case 'running':
          return <Loading small={true} />;
        case 'success':
          return (
            <button
              onClick={showDeleteDialog}
              data-target={fileState.file.id}
              className="text-gray-500 hover:text-blue-100 focus:text-blue-100"
            >
              <div className="w-6 h-6 fill-current">
                <Icon name="Delete" />
              </div>
            </button>
          );
        case 'error':
          return (
            <div className="w-4 h-4 fill-current text-gray-500">
              <Icon name="Error" />
            </div>
          );
      }
    },
    [showDeleteDialog],
  );

  const onConfirm = useCallback(() => {
    deleteFile(fileToBeDeleted);
    setFileToBeDeleted(null);
  }, [deleteFile, fileToBeDeleted, setFileToBeDeleted]);

  const onCancel = useCallback(() => {
    setIsModalHidden(true);
    setFileToBeDeleted(null);
  }, [setFileToBeDeleted, setIsModalHidden]);

  const attachedFiles = useMemo(
    () => (
      <>
        <Label content={t('components:fileUpload:fileListLabel')} />
        <div className="border-solid border-t border-gray-400">
          {currentFileStates.map((fileState, index) => (
            <div
              key={index}
              className="flex items-center justify-between py-6 border-solid h-7 border-b border-gray-400"
            >
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 fill-current text-gray-800">
                  <Icon name="File" />
                </div>
                <p className="mt-1 font-regular">{fileState.file.adjustedName}</p>
              </div>
              {getUploadFeedback(fileState)}
            </div>
          ))}
        </div>
      </>
    ),
    [currentFileStates, getUploadFeedback, t],
  );

  return (
    <div>
      <Modal
        icon="Delete"
        heading={t('modals:deleteUploadedFile:prompt')}
        isHidden={isModalHidden}
        confirmText={t('modals:deleteUploadedFile:confirm')}
        cancelText={t('modals:deleteUploadedFile:cancel')}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
      <form ref={fileUploadFormRef}>
        <input
          ref={fileUploadRef}
          multiple={true}
          type="file"
          name="fileUpload"
          onChange={onFileSelected}
          className="hidden"
        />
      </form>
      {currentFileStates.length > 0 ? attachedFiles : null}
      <div className="flex items-center justify-between py-2 mt-3 h-7">
        <button
          // htmlFor={id}
          data-testid="upload-button"
          className="inline-flex items-center space-x-1 text-base font-regular text-blue-200 cursor-pointer focus:outline-none hover:text-blue-100 focus:text-blue-100"
          onClick={() => {
            if (fileUploadFormRef?.current) {
              // Clear the file input value to trigger an onChange event, even if the user selects the
              // same file again.
              // This is required to cover the case, that the user might upload a file, accidentaly
              // delete it and with to reupload the same file again.
              (fileUploadFormRef.current as unknown as HTMLFormElement).reset();
            }
            if (fileUploadRef?.current) {
              (fileUploadRef.current as unknown as HTMLInputElement).click();
            }
          }}
        >
          <div className="w-4 h-4">
            <Icon name="Add" />
          </div>
          <p>{label}</p>
        </button>
      </div>
    </div>
  );
};
